/**
 * simulateur-premium.jsx - Simulateur avancé avec scénarios et paliers
 * 
 * Version premium avec 4 champs de budget et comparaison de scénarios.
 * 
 * Fonctionnalités :
 * - 4 champs : Revenu, Dépenses fixes, Épargne actuelle, Objectif
 * - Calcul de la capacité d'épargne mensuelle (Revenu - Dépenses)
 * - Comparaison de 5 scénarios (+50/+100 revenus, -50/-100 dépenses)
 * - Paliers de progression (25%, 50%, 75%, 100%) avec dates estimées
 * - Évaluation de faisabilité (ok/mid/bad)
 * - Graphique d'évolution via ChartResults
 * - Aperçu des fonctionnalités PRO verrouillées
 * 
 * Calcul :
 * - Épargne mensuelle = Revenu - Dépenses fixes
 * - Mois nécessaires = (Objectif - Épargne actuelle) / Épargne mensuelle
 * - Pas d'intérêts composés (version simple linéaire)
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./premium-simulateur.css";
import ChartResults from "../components/chartResults";
import { useSEO } from "../seo/useSEO";

/* ========= Devises ========= */
/**
 * Devises supportées (identiques au simulateur simple)
 */
const CURRENCIES = [
  { code: "EUR", label: "€ Euro" },
  { code: "USD", label: "$ Dollar" },
  { code: "GBP", label: "£ Livre" },
  { code: "XOF", label: "F CFA (XOF)" },
];

/**
 * Formateur de montant monétaire
 * @param {string} currency - Code ISO de la devise
 * @returns {Intl.NumberFormat} Formateur configuré
 */
function moneyFormatter(currency) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  });
}

/* ========= Utils ========= */

/**
 * Limite une valeur entre min et max
 */
const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

/**
 * Convertit un nombre de mois en texte lisible
 * @param {number} total - Nombre de mois
 * @returns {string} Texte formaté (ex: "2 ans et 5 mois")
 */
function monthsToHuman(total) {
  if (!isFinite(total)) return "∞";
  const y = Math.floor(total / 12);
  const m = total % 12;
  if (y && m) return `${y} an${y > 1 ? "s" : ""} et ${m} mois`;
  if (y) return `${y} an${y > 1 ? "s" : ""}`;
  return `${m} mois`;
}

/**
 * Ajoute un nombre de mois à une date
 * @param {Date} date - Date de départ
 * @param {number} months - Nombre de mois à ajouter
 * @returns {Date} Nouvelle date
 */
function addMonths(date, months) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

/**
 * Formate une date en "mois année" (ex: "janvier 2027")
 * @param {Date} date - Date à formater
 * @returns {string} Date formatée
 */
function formatMonthYear(date) {
  return date.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
}

/* ========= Core calc ========= */

/**
 * Calcule le nombre de mois pour atteindre l'objectif (sans intérêts composés)
 * 
 * Formule simple linéaire :
 * - Capacité d'épargne mensuelle = Revenu - Dépenses fixes
 * - Mois nécessaires = (Objectif - Épargne actuelle) / Capacité mensuelle
 * 
 * @param {Object} params - Paramètres du budget
 * @param {number} params.income - Revenu mensuel
 * @param {number} params.fixed - Dépenses fixes mensuelles
 * @param {number} params.current - Épargne actuelle
 * @param {number} params.goal - Objectif à atteindre
 * @returns {Object} Résultat
 * @returns {number} return.monthlySave - Capacité d'épargne mensuelle
 * @returns {number} return.monthsToGoal - Nombre de mois (Infinity si impossible)
 * @returns {boolean} return.reachable - True si objectif atteignable
 */
function computeMonthsToGoal({ income, fixed, current, goal }) {
  const monthlySave = Math.max(0, income - fixed); // Capacité d'épargne
  const start = Math.max(0, current);
  const g = Math.max(0, goal);

  // Cas 1 : Objectif déjà atteint
  if (g <= start) return { monthlySave, monthsToGoal: 0, reachable: true };
  
  // Cas 2 : Aucune capacité d'épargne (dépenses >= revenus)
  if (monthlySave <= 0) return { monthlySave, monthsToGoal: Infinity, reachable: false };

  // Cas 3 : Calcul linéaire
  const remaining = g - start;
  const months = Math.ceil(remaining / monthlySave);
  return { monthlySave, monthsToGoal: months, reachable: true };
}

/* ========= Scenario generator ========= */

/**
 * Génère 5 scénarios de comparaison pour visualiser l'impact de changements
 * 
 * Scénarios :
 * 1. base : Situation actuelle (référence)
 * 2. plus50 : +50€ de revenu mensuel
 * 3. plus100 : +100€ de revenu mensuel
 * 4. cut50 : -50€ de dépenses mensuelles
 * 5. cut100 : -100€ de dépenses mensuelles
 * 
 * @param {Object} params - Paramètres du budget
 * @param {number} params.income - Revenu mensuel
 * @param {number} params.fixed - Dépenses fixes mensuelles
 * @param {number} params.current - Épargne actuelle
 * @param {number} params.goal - Objectif
 * @returns {Array<Object>} Tableau de scénarios avec métriques
 * @returns {string} return[].key - Identifiant unique du scénario
 * @returns {string} return[].label - Libellé affiché
 * @returns {number} return[].monthlySave - Capacité d'épargne mensuelle
 * @returns {number} return[].monthsToGoal - Mois pour atteindre l'objectif
 * @returns {boolean} return[].reachable - Objectif atteignable
 * @returns {number} return[].monthsSaved - Mois gagnés vs scénario base
 */
function buildScenarios({ income, fixed, current, goal }) {
  // Calculer le scénario de référence
  const base = computeMonthsToGoal({ income, fixed, current, goal });

  // Définir les 5 scénarios à comparer
  const scenarios = [
    { key: "base", label: "Actuel", incomeDelta: 0, fixedDelta: 0 },
    { key: "plus50", label: "+50/mois", incomeDelta: 50, fixedDelta: 0 },
    { key: "plus100", label: "+100/mois", incomeDelta: 100, fixedDelta: 0 },
    { key: "cut50", label: "-50 dépenses", incomeDelta: 0, fixedDelta: -50 },
    { key: "cut100", label: "-100 dépenses", incomeDelta: 0, fixedDelta: -100 },
  ];

  // Calculer chaque scénario avec les deltas appliqués
  return scenarios.map((s) => {
    const out = computeMonthsToGoal({
      income: income + s.incomeDelta,  // Appliquer le delta de revenu
      fixed: fixed + s.fixedDelta,      // Appliquer le delta de dépenses
      current,
      goal,
    });
    return {
      ...s,
      monthlySave: out.monthlySave,
      monthsToGoal: out.monthsToGoal,
      reachable: out.reachable,
      // Calcul du gain : combien de mois gagnés par rapport au scénario base
      monthsSaved:
        isFinite(base.monthsToGoal) && isFinite(out.monthsToGoal)
          ? base.monthsToGoal - out.monthsToGoal
          : 0,
    };
  });
}

/* ========= Curve points for ChartResults =========
 * Génère les points pour le graphique (format attendu par ChartResults)
 * ChartResults attend: [{ month: number, amount: number }]
 * 
 * On génère un point par mois avec une progression linéaire simple :
 * - amount(n) = current + (monthlySave * n)
 * - Cap à 1200 mois (100 ans) pour éviter les boucles infinies
 */

/**
 * Génère les points de la courbe d'évolution pour le graphique
 * @param {Object} params - Paramètres
 * @param {number} params.current - Épargne actuelle
 * @param {number} params.goal - Objectif
 * @param {number} params.monthlySave - Épargne mensuelle
 * @returns {Array<{month: number, amount: number}>} Points pour ChartResults
 */
function buildCurvePoints({ current, goal, monthlySave }) {
  const start = Math.max(0, current);
  const g = Math.max(0, goal);
  const m = Math.max(0, monthlySave);

  const points = [{ month: 0, amount: start }]; // Point de départ

  // Cas où l'objectif est déjà atteint ou aucune capacité d'épargne
  if (g <= start) return points;
  if (m <= 0) return points;

  let amount = start;
  let month = 0;

  // Générer un point par mois jusqu'à atteindre l'objectif (max 1200 mois)
  while (amount < g && month < 1200) {
    month++;
    amount += m; // Progression linéaire simple (pas d'intérêts)
    points.push({ month, amount });
  }

  return points;
}

/* ========= Locked UI wrapper ========= */

/**
 * Composant Locked - Wrapper pour les fonctionnalités premium verrouillées
 * 
 * Affiche un aperçu bluré de la fonctionnalité avec un overlay de déblocage.
 * Utilisé pour montrer ce qui est disponible dans la version payante.
 * 
 * @param {Object} props - Props du composant
 * @param {string} props.title - Titre de la fonctionnalité
 * @param {string} props.desc - Description courte
 * @param {React.ReactNode} props.children - Aperçu de la fonctionnalité (sera bluré)
 * @returns {JSX.Element} Composant avec overlay
 */
function Locked({ title, desc, children }) {
  return (
    <div className="ps-locked">
      <div className="ps-locked-head">
        <div className="ps-locked-title">{title}</div>
        <div className="ps-locked-badge">PRO</div>
      </div>
      <div className="ps-locked-desc">{desc}</div>
      <div className="ps-locked-body">{children}</div>
      <div className="ps-locked-overlay">
        <div className="ps-locked-overlay-card">
          <div className="ps-locked-overlay-title">Fonctionnalité premium</div>
          <div className="ps-locked-overlay-text">
            Débloque en payant <b>1€ / simulation</b> ou via <b>abonnement illimité</b>.
          </div>
          <button className="ps-btn ps-btn-primary" type="button" disabled>
            Débloquer (bientôt)
          </button>
        </div>
      </div>
    </div>
  );
}

/* ========= Page ========= */

/**
 * Composant PremiumSimulateur - Simulateur avancé avec scénarios et paliers
 * 
 * État :
 * - currency : Devise sélectionnée
 * - income : Revenu mensuel
 * - fixed : Dépenses fixes mensuelles
 * - current : Épargne actuelle
 * - goal : Objectif financier
 * 
 * Calculs dérivés (useMemo) :
 * - base : Calcul principal (mois + capacité d'épargne)
 * - scenarios : 5 scénarios de comparaison
 * - points : Points pour le graphique ChartResults
 * - reachedDate : Date d'atteinte de l'objectif
 * - milestones : Paliers 25%, 50%, 75%, 100%
 * - feasibility : Évaluation de faisabilité (ok/mid/bad)
 * 
 * @returns {JSX.Element} Page du simulateur premium
 */
export default function PremiumSimulateur() {
  const [currency, setCurrency] = useState("EUR");

  useSEO({
    title: "Simulateur premium — Scénarios & paliers | HowMuch",
    description:
      "Compare des scénarios (+50/+100 revenus, -50/-100 dépenses), vois les paliers (25/50/75/100%) et estime la date d’atteinte de ton objectif.",
    canonicalPath: "/simulate/premium",
    ogType: "website",
  });

  // 4 champs principaux du budget
  const [income, setIncome] = useState("2500");   // Revenu mensuel
  const [fixed, setFixed] = useState("1800");     // Dépenses fixes
  const [current, setCurrent] = useState("2000"); // Épargne actuelle
  const [goal, setGoal] = useState("20000");      // Objectif

  // Formateur monétaire selon la devise
  const MONEY = useMemo(() => moneyFormatter(currency), [currency]);

  // Calcul principal : combien de mois pour atteindre l'objectif ?
  const base = useMemo(
    () => computeMonthsToGoal({ income, fixed, current, goal }),
    [income, fixed, current, goal]
  );

  // Génération des 5 scénarios de comparaison
  const scenarios = useMemo(
    () => buildScenarios({ income, fixed, current, goal }),
    [income, fixed, current, goal]
  );

  // Génération des points pour le graphique ChartResults
  const points = useMemo(
    () => buildCurvePoints({ current, goal, monthlySave: base.monthlySave }),
    [current, goal, base.monthlySave]
  );

  // Calcul de la date d'atteinte de l'objectif
  const reachedDate = useMemo(() => {
    if (!isFinite(base.monthsToGoal)) return "—";
    return formatMonthYear(addMonths(new Date(), base.monthsToGoal));
  }, [base.monthsToGoal]);

  // Calcul des 4 paliers de progression (25%, 50%, 75%, 100%)
  const milestones = useMemo(() => {
    const g = Math.max(1, goal);
    const start = Math.max(0, current);
    const m = Math.max(0, base.monthlySave);

    const pctTargets = [0.25, 0.5, 0.75, 1.0]; // Les 4 paliers
    return pctTargets.map((p) => {
      const target = g * p; // Montant cible pour ce palier
      
      // Déjà atteint
      if (start >= target) return { pct: p, months: 0, label: "Aujourd'hui" };
      
      // Impossible (pas de capacité d'épargne)
      if (m <= 0) return { pct: p, months: Infinity, label: "—" };

      // Calcul du nombre de mois pour ce palier
      const months = Math.ceil((target - start) / m);
      return { pct: p, months, label: formatMonthYear(addMonths(new Date(), months)) };
    });
  }, [goal, current, base.monthlySave]);

  // Évaluation de la faisabilité du plan (basée sur le ratio épargne/revenu)
  const feasibility = useMemo(() => {
    const monthly = base.monthlySave;
    
    // Objectif déjà atteint
    if (goal <= current) return { level: "ok", text: "Objectif déjà atteint 🎉" };
    
    // Aucune capacité d'épargne (dépenses >= revenus)
    if (monthly <= 0) return { level: "bad", text: "Aucune capacité d'épargne. Ajuste tes dépenses." };

    // Évaluation basée sur le ratio épargne/revenu
    const ratio = monthly / Math.max(1, income);
    
    // < 15% du revenu : facile
    if (ratio < 0.15) return { level: "ok", text: "Réalisable sans trop d'effort." };
    
    // 15-30% du revenu : modéré
    if (ratio < 0.30) return { level: "mid", text: "Réalisable, mais ça demande de la régularité." };
    
    // > 30% du revenu : difficile
    return { level: "bad", text: "Tendu : grosse part de ton budget chaque mois." };
  }, [base.monthlySave, goal, current, income]);

  return (
    <main className="ps">
      <header className="ps-header">
        <div className="ps-container ps-header-inner">
          <div className="ps-brand">
            <div className="ps-logo">HM</div>
            <div className="ps-brand-text">
              <div className="ps-brand-name">HowMuch</div>
              <div className="ps-brand-tag">Simulations avancées</div>
            </div>
          </div>

          <div className="ps-header-actions">
            <Link to="/simulate" className="ps-btn ps-btn-ghost">
              ← Retour simple
            </Link>

            <Link to="/blog" className="ps-btn ps-btn-ghost">
              Blog
            </Link>

            <select className="ps-select" value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </header>

      <section className="ps-container ps-top">
        <div className="ps-title">
          <h1>Simulations premium</h1>
          <p>Compare des scénarios, comprends l’impact de tes choix, et trouve le plan le plus réaliste.</p>
        </div>

        <div className="ps-grid">
          {/* Formulaire des 4 champs de budget */}
          <aside className="ps-card">
            <div className="ps-card-head">
              <div>
                <div className="ps-card-title">Ton budget (4 champs)</div>
                <div className="ps-card-note">Aucun jargon — juste l’essentiel.</div>
              </div>
            </div>

            <div className="ps-fields">
              <label className="ps-label">
                Revenu mensuel
                <input
                  className="ps-input"
                  type="number"
                  min="0"
                  value={income}
                  onChange={(e) => setIncome(e.target.value)}
                />
              </label>

              <label className="ps-label">
                Dépenses fixes mensuelles
                <input
                  className="ps-input"
                  type="number"
                  min="0"
                  value={fixed}
                  onChange={(e) => setFixed(e.target.value)}
                />
              </label>

              <label className="ps-label">
                Épargne actuelle
                <input
                  className="ps-input"
                  type="number"
                  min="0"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                />
              </label>

              <label className="ps-label">
                Objectif
                <input
                  className="ps-input"
                  type="number"
                  min="0"
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                />
              </label>

              <div className="ps-mini">
                <div className="ps-mini-row">
                  <span>Capacité d’épargne</span>
                  <b>{MONEY.format(base.monthlySave)}</b>
                </div>
                <div className="ps-mini-row">
                  <span>Reste à vivre (approx.)</span>
                  <b>{MONEY.format(Math.max(0, income - fixed))}</b>
                </div>
              </div>
            </div>
          </aside>

          {/* Results */}
          <div className="ps-right">
            {/* WOW result */}
            <section className="ps-card ps-wow">
              <div className="ps-wow-inner">
                <div className="ps-wow-label">Résultat</div>
                <div className="ps-wow-title">
                  🎯 Objectif atteint en <span>{reachedDate}</span>
                </div>

                <div className="ps-wow-sub">
                  Épargne mensuelle calculée : <b>+{MONEY.format(base.monthlySave)}</b> · Temps restant :{" "}
                  <b>{monthsToHuman(base.monthsToGoal)}</b>
                </div>

                <div className={`ps-feas ps-feas-${feasibility.level}`}>
                  <div className="ps-feas-title">Faisabilité</div>
                  <div className="ps-feas-text">{feasibility.text}</div>
                </div>
              </div>
            </section>

            {/* Scenarios */}
            <section className="ps-card">
              <div className="ps-card-head">
                <div>
                  <div className="ps-card-title">Comparaison de scénarios</div>
                  <div className="ps-card-note">“Et si tu changeais une seule chose ?”</div>
                </div>
              </div>

              <div className="ps-scenarios">
                {scenarios.map((s) => (
                  <div className="ps-scenario" key={s.key}>
                    <div className="ps-scenario-left">
                      <div className="ps-scenario-name">{s.label}</div>
                      <div className="ps-scenario-muted">
                        Mensualité estimée : <b>{MONEY.format(s.monthlySave)}</b>
                      </div>
                    </div>

                    <div className="ps-scenario-right">
                      <div className="ps-scenario-months">{monthsToHuman(s.monthsToGoal)}</div>
                      {s.key !== "base" && isFinite(s.monthsSaved) ? (
                        <div className="ps-scenario-gain">{s.monthsSaved > 0 ? `-${s.monthsSaved} mois` : "—"}</div>
                      ) : (
                        <div className="ps-scenario-gain ps-scenario-gain-muted">référence</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Chart (✅ via ChartResults) */}
            <section className="ps-card">
              <div className="ps-card-head">
                <div>
                  <div className="ps-card-title">Évolution</div>
                  <div className="ps-card-note">Survole une barre pour voir le montant + mensualité.</div>
                </div>
              </div>

              <div className="ps-chart-wrap">
                <ChartResults
                  data={points}
                  goal={goal}
                  currency={currency}
                  monthly={base.monthlySave}
                />
              </div>
            </section>

            {/* Milestones */}
            <section className="ps-card">
              <div className="ps-card-head">
                <div>
                  <div className="ps-card-title">Paliers</div>
                  <div className="ps-card-note">Pour rester motivé, tu vois les étapes.</div>
                </div>
              </div>

              <div className="ps-milestones">
                {milestones.map((m) => (
                  <div className="ps-ms" key={m.pct}>
                    <div className="ps-ms-left">
                      <div className="ps-ms-badge">{Math.round(m.pct * 100)}%</div>
                      <div className="ps-ms-date">{m.label}</div>
                    </div>
                    <div className="ps-ms-right">{isFinite(m.months) ? `+${monthsToHuman(m.months)}` : "—"}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Locked premium features (greyed) */}
            <Locked
              title="Objectif inversé (atteindre en une date)"
              desc="Choisis une date cible, on te calcule la mensualité nécessaire."
            >
              <div className="ps-locked-demo">
                <div className="ps-locked-row">
                  <span>Date cible</span>
                  <span className="ps-pill">Mars 2027</span>
                </div>
                <div className="ps-locked-row">
                  <span>Mensualité nécessaire</span>
                  <span className="ps-pill">620</span>
                </div>
              </div>
            </Locked>

            <Locked
              title="Export PDF + Comparateur illimité"
              desc="Télécharge ton plan (PDF) et fais autant de simulations que tu veux."
            >
              <div className="ps-locked-demo">
                <div className="ps-locked-row">
                  <span>Export</span>
                  <span className="ps-pill">PDF</span>
                </div>
                <div className="ps-locked-row">
                  <span>Simulations</span>
                  <span className="ps-pill">Illimité</span>
                </div>
                <div className="ps-locked-row">
                  <span>Budgets affinés</span>
                  <span className="ps-pill">Catégories</span>
                </div>
              </div>
            </Locked>
          </div>
        </div>
      </section>
    </main>
  );
}
